import { Link } from 'react-router';
import type { TicketDetail } from '../../../api/ticket-types';
import { Badge, StateBadge } from '../../../ui/Badge';
import { Button } from '../../../ui/Button';
import { ActorName, Card } from '../../../ui/misc';
import { Timestamp } from '../../../ui/Timestamp';

export function Implementation({ t, onStart }: { t: TicketDetail; onStart: () => void }) {
  const base = `/projects/${t.project.key}/tickets/${t.number}`;
  return (
    <>
      <Card
        title="Implementation attempts"
        subtitle="Each claim is a new generation. Work references carry over to the next attempt."
        action={
          t.viewer.canStartManual && (
            <Button size="sm" variant="secondary" onClick={onStart}>
              Take manual ownership
            </Button>
          )
        }
      >
        {t.implementations.length === 0 ? (
          <span className="small muted">
            {t.status === 'todo'
              ? t.blocked
                ? 'Blocked: resolve blockers before work can start.'
                : t.assignee
                  ? `Assigned to ${t.assignee.name}; not AI-claimable.`
                  : 'Eligible for an AI claim.'
              : 'No implementation yet.'}
          </span>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Attempt</th>
                  <th>Owner</th>
                  <th>State</th>
                  <th>Detail</th>
                  <th>Started</th>
                </tr>
              </thead>
              <tbody>
                {t.implementations.map((i) => (
                  <tr key={i.id}>
                    <td>
                      <Link to={`${base}/implementations/${i.displayId}`} className="mono">
                        {i.displayId}
                      </Link>
                    </td>
                    <td>
                      <ActorName actor={i.owner} />
                    </td>
                    <td>
                      <StateBadge state={i.state} label={i.state === 'stale' ? 'Stale owner' : undefined} />
                    </td>
                    <td className="small">
                      gen {i.generation} · scope rev {i.scopeRevision}
                      {i.branch ? ` · ${i.branch}` : ''}
                      {i.terminationReason ? ` · ${i.terminationReason}` : ''}
                    </td>
                    <td className="small">
                      <Timestamp value={i.startedAt} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <Card
        title="Readiness snapshots"
        subtitle="Frozen when work enters Pending release. Readiness does not authorize deployment."
      >
        {t.readiness.length === 0 ? (
          <span className="small muted">None yet.</span>
        ) : (
          <ul className="checklist">
            {t.readiness.map((r) => (
              <li key={r.id} className="small">
                <span className="mono">{r.sourceRevision.slice(0, 12)}</span> · scope rev {r.scopeRevision} ·{' '}
                <Timestamp value={r.createdAt} />
                {r.invalidatedAt ? (
                  <Badge>Invalidated: {r.invalidatedReason}</Badge>
                ) : (
                  <Badge tone="success">Current</Badge>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
